import { useEffect, useState } from 'react';
import { normalizeAttributes } from '@prakash-software/shared';
import { isApiError } from '../lib/api';
import { getContactAttributes, saveContactAttributes } from '../lib/flows-api';
import styles from './ContactAttributes.module.css';

// A contact's customer attributes: facts a Flow saved ("Set attribute") or an
// agent added — plan, order id, preferred time. Flows read them back as
// {{contact.<key>}} in later conversations.

interface Row {
  key: string;
  value: string;
}

const toRows = (a: Record<string, string>): Row[] => Object.entries(a).map(([key, value]) => ({ key, value }));

export function ContactAttributes({ siteId, contactId }: { siteId: string; contactId: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setRows(null);
    getContactAttributes(siteId, contactId)
      .then((r) => {
        if (!live) return;
        setSaved(r.attributes);
        setRows(toRows(r.attributes));
      })
      .catch(() => live && setError('Could not load attributes.'));
    return () => {
      live = false;
    };
  }, [siteId, contactId]);

  if (rows === null) return error ? <p className={styles.error}>{error}</p> : null;

  const draft = Object.fromEntries(rows.filter((r) => r.key.trim()).map((r) => [r.key.trim().toLowerCase(), r.value]));
  const check = normalizeAttributes(draft);
  const changed = JSON.stringify(check.ok ? check.attributes : draft) !== JSON.stringify(saved);

  const save = async () => {
    if (!check.ok) return;
    setBusy(true);
    setError(null);
    try {
      const r = await saveContactAttributes(siteId, contactId, check.attributes);
      setSaved(r.attributes);
      setRows(toRows(r.attributes));
    } catch (err) {
      const msg = isApiError(err) ? (err.body as { message?: string } | null)?.message : undefined;
      setError(msg ?? 'Could not save attributes.');
    } finally {
      setBusy(false);
    }
  };

  const patch = (i: number, p: Partial<Row>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));

  return (
    <div className={styles.box}>
      <p className={styles.heading}>Customer attributes</p>
      {rows.length === 0 ? <p className={styles.note}>None yet. Flows can set them with a “Set attribute” step.</p> : null}
      {rows.map((r, i) => (
        <div key={i} className={styles.row}>
          <input className={styles.input} aria-label="Attribute key" value={r.key} placeholder="plan" onChange={(e) => patch(i, { key: e.target.value })} />
          <input className={styles.input} aria-label={`Value of ${r.key || 'attribute'}`} value={r.value} onChange={(e) => patch(i, { value: e.target.value })} />
          <button type="button" className={styles.button} aria-label={`Remove ${r.key || 'attribute'}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}>
            ×
          </button>
        </div>
      ))}
      <div className={styles.actions}>
        <button type="button" className={styles.button} onClick={() => setRows([...rows, { key: '', value: '' }])}>
          + Attribute
        </button>
        <button type="button" className={styles.button} disabled={!changed || !check.ok || busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {!check.ok ? <span className={styles.error}>{check.error}</span> : null}
        {error ? <span className={styles.error}>{error}</span> : null}
      </div>
    </div>
  );
}
